// DSN-free client error reporting. Relays browser crashes to the backend
// (POST /v1/public/client-error), which emits Google Cloud Error Reporting
// events — the same place API errors land. No SDK, no DSN, no third party.
import { env } from '../env';

const API = env.VITE_API_BASE_URL;

type Extra = {
  kind?: 'react' | 'window.onerror' | 'unhandledrejection' | 'bootstrap-stall';
  componentStack?: string;
};

/** Report a client-side error. Best-effort and never throws. */
export function reportClientError(err: unknown, extra: Extra = {}): void {
  try {
    const e =
      err instanceof Error
        ? err
        : new Error(typeof err === 'string' ? err : 'Unknown client error');
    const body = JSON.stringify({
      message: (e.message || 'Unknown client error').slice(0, 2000),
      stack: e.stack?.slice(0, 20000),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      kind: extra.kind,
      componentStack: extra.componentStack?.slice(0, 20000),
    });
    // keepalive lets the POST survive an unload/crash that follows the error.
    void fetch(`${API}/v1/public/client-error`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* reporting must never break the app */
  }
}

let installed = false;
/** Catch errors that escape React (async, event handlers, promises). */
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (ev) => {
    reportClientError(ev.error ?? new Error(ev.message), { kind: 'window.onerror' });
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = (ev as PromiseRejectionEvent).reason;
    reportClientError(reason instanceof Error ? reason : new Error(String(reason)), {
      kind: 'unhandledrejection',
    });
  });
}
